import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import { infantBaptisms, firstCommunions, baptismConfirmations } from '../../api/endpoints';
import { useFetchList, useMemberDirectory, useChurchConfig } from '../../hooks';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge, SummaryNote
} from '../../components/ui';
import type { BadgeTone } from '../../components/ui';
import type { InfantBaptism, FirstCommunion, BaptismConfirmation } from '../../types';
import { baptismSummary } from './baptismAnalytics';
import type { SacramentKind } from './baptismAnalytics';

// The Baptism register's Summary tab. The register list itself is server-paged,
// so this reads each of the three register tables whole (their list endpoints
// return every row) and counts across all live entries, not one page.

const KIND_TONE: Record<SacramentKind, BadgeTone> = { Baptism: 'accent', Communion: 'warning', Confirmation: 'success' };

/** Fallbacks when the church configuration has never been saved — as Members › Sacraments. */
const DEFAULT_LIMITS = { baptismDueMonths: 12, communionDueAge: 13, confirmationDueAge: 13 };

/** How many due members the watch-list shows before pointing to Members › Sacraments. */
const DUE_LIST_LIMIT = 20;

const BaptismSummaryView = ({ isDarkMode }: { isDarkMode?: boolean }) => {
  const t = useStyles(isDarkMode).t;
  const baptisms = useFetchList<InfantBaptism>((p, o) => infantBaptisms.list(p, o));
  const communions = useFetchList<FirstCommunion>((p, o) => firstCommunions.list(p, o));
  const confirmations = useFetchList<BaptismConfirmation>((p, o) => baptismConfirmations.list(p, o));
  const { members, loading: membersLoading } = useMemberDirectory();
  const config = useChurchConfig();
  const loading = baptisms.loading || communions.loading || confirmations.loading;

  const limits = {
    baptismDueMonths: Number(config.baptismDueMonths) || DEFAULT_LIMITS.baptismDueMonths,
    communionDueAge: Number(config.communionDueAge) || DEFAULT_LIMITS.communionDueAge,
    confirmationDueAge: Number(config.confirmationDueAge) || DEFAULT_LIMITS.confirmationDueAge
  };

  const s = useMemo(
    () => baptismSummary(baptisms.data, communions.data, confirmations.data, members, limits),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baptisms.data, communions.data, confirmations.data, members,
      limits.baptismDueMonths, limits.communionDueAge, limits.confirmationDueAge]
  );

  const kindColor = (k: SacramentKind) => (k === 'Baptism' ? t.info : k === 'Communion' ? t.warning : t.income);
  const glance = [
    { kind: 'Baptism' as SacramentKind, total: s.totals.baptism, thisYear: s.thisYear.baptism, lastYear: s.lastYear.baptism },
    { kind: 'Communion' as SacramentKind, total: s.totals.communion, thisYear: s.thisYear.communion, lastYear: s.lastYear.communion },
    { kind: 'Confirmation' as SacramentKind, total: s.totals.confirmation, thisYear: s.thisYear.confirmation, lastYear: s.lastYear.confirmation }
  ];
  const monthPeak = peakOf(s.months, (r) => r.total);
  const yearPeak = peakOf(s.years, (r) => r.total);
  const officiantPeak = peakOf(s.officiants, (r) => r.total);
  const year = new Date().getFullYear();
  const splitCols = [
    { key: 'baptism', header: 'Bap.', align: 'right' as const },
    { key: 'communion', header: 'Comm.', align: 'right' as const },
    { key: 'confirmation', header: 'Conf.', align: 'right' as const }
  ];
  const entryCols = [
    { key: 'kind', header: 'Register', nowrap: true, render: (r) => <Badge tone={KIND_TONE[r.kind]}>{r.kind}</Badge> },
    { key: 'recordId', header: 'Record', nowrap: true, render: (r) => <strong>{r.recordId || '-'}</strong> },
    { key: 'name', header: 'Name', render: (r) => r.name || '-' },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => (r.date ? formatDate(r.date) : '-') }
  ];

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Baptisms" value={String(s.totals.baptism)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="First Communions" value={String(s.totals.communion)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Confirmations" value={String(s.totals.confirmation)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`Baptisms ${year}`} value={String(s.thisYear.baptism)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label={`Communions ${year}`} value={String(s.thisYear.communion)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label={`Confirmations ${year}`} value={String(s.thisYear.confirmation)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Due for Baptism" value={membersLoading ? '—' : String(s.due.baptism)} color={s.due.baptism ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Due for Communion" value={membersLoading ? '—' : String(s.due.communion)} color={s.due.communion ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Due for Confirmation" value={membersLoading ? '—' : String(s.due.confirmation)} color={s.due.confirmation ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Missing Numbers" value={String(s.missingNumbers.length)} color={s.missingNumbers.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Not Linked to Member" value={String(s.notLinked.length)} color={s.notLinked.length ? t.warning : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Sacraments at a Glance"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'kind', header: 'Sacrament', render: (r) => <strong>{r.kind}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.total, s.totals.all)} max={100} showPercent color={kindColor(r.kind)} isDarkMode={isDarkMode} /> },
            { key: 'lastYear', header: String(year - 1), align: 'right' },
            { key: 'thisYear', header: String(year), align: 'right' },
            { key: 'total', header: 'All Time', align: 'right' }
          ]}
          rows={glance}
          rowKey="kind"
          below={(
            <>
              <TotalRow label="Total entries" value={s.totals.all} isDarkMode={isDarkMode} />
              {s.avgBaptismAgeMonths !== null && (
                <SummaryNote isDarkMode={isDarkMode}>
                  Average age at baptism: {s.avgBaptismAgeMonths} month{s.avgBaptismAgeMonths === 1 ? '' : 's'}.
                </SummaryNote>
              )}
            </>
          )}
        />
        <TablePanel
          title="By Minister / Bishop"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Officiant', render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={officiantPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            ...splitCols,
            { key: 'total', header: 'Total', align: 'right', render: (r) => <strong>{r.total}</strong> }
          ]}
          rows={s.officiants}
          rowKey="key"
          emptyMessage="No register entries yet."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Last 12 Months"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
            ...splitCols,
            { key: 'total', header: 'Total', align: 'right', render: (r) => <strong>{r.total}</strong> }
          ]}
          rows={s.months}
          rowKey="key"
          below={<TotalRow label="Last 12 months" value={s.months.reduce((n, m) => n + m.total, 0)} isDarkMode={isDarkMode} />}
        />
        <TablePanel
          title="By Year"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Year', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={yearPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            ...splitCols,
            { key: 'total', header: 'Total', align: 'right', render: (r) => <strong>{r.total}</strong> }
          ]}
          rows={s.years}
          rowKey="key"
          emptyMessage="No dated entries yet."
          below={s.years.length ? <SummaryNote isDarkMode={isDarkMode}>The ten most recent years with entries.</SummaryNote> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Recent Entries — Last 90 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          ...entryCols,
          { key: 'officiant', header: 'Minister / Bishop', render: (r) => r.officiant || '-' },
          { key: 'certificateNo', header: 'Certificate', nowrap: true, render: (r) => r.certificateNo || '-' }
        ]}
        rows={s.recent}
        rowKey={(r) => `${r.kind}|${r.recordId}`}
        emptyMessage="Nothing entered in the last 90 days."
      />

      <TablePanel
        title="Members Due for a Sacrament"
        isDarkMode={isDarkMode}
        loading={membersLoading}
        minWidth="480px"
        columns={[
          { key: 'name', header: 'Member', render: (r) => <strong>{r.name || '-'}</strong> },
          { key: 'memberId', header: 'Member ID', nowrap: true, render: (r) => r.memberId || '-' },
          { key: 'ageYears', header: 'Age', align: 'right', render: (r) => (r.ageYears === null ? '-' : r.ageYears) },
          {
            key: 'stages', header: 'Due',
            render: (r) => r.stages.map((k) => <Badge key={k} tone="warning" style={{ marginRight: '4px' }}>{k}</Badge>)
          }
        ]}
        rows={s.due.members.slice(0, DUE_LIST_LIMIT)}
        rowKey={(r) => r.memberId || r.name}
        emptyMessage="No member is due for baptism, communion or confirmation."
        below={s.due.members.length > DUE_LIST_LIMIT ? (
          <SummaryNote isDarkMode={isDarkMode}>
            Showing the eldest {DUE_LIST_LIMIT} of {s.due.members.length}. Members › Sacraments has the full list.
          </SummaryNote>
        ) : null}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Missing Register / Certificate No."
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            ...entryCols,
            { key: 'missing', header: 'Missing', render: (r) => <Badge tone="error">{r.missing.join(', ')}</Badge> }
          ]}
          rows={s.missingNumbers}
          rowKey={(r) => `${r.kind}|${r.recordId}`}
          emptyMessage="Every entry has its register and certificate numbers."
        />
        <TablePanel
          title="Not Linked to a Member"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={entryCols}
          rows={s.notLinked}
          rowKey={(r) => `${r.kind}|${r.recordId}`}
          emptyMessage="Every entry is linked to a member."
        />
      </SummaryColumns>
    </>
  );
};

export default BaptismSummaryView;
